import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Tooltip,
  Typography,
  useMediaQuery,
  styled,
  Skeleton,
  Badge,
  Card,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import Notification from '../Notification/Notification';
import UserNameAvatar, { ILoggedUser, UserType } from '../User/User';
import { getTitle, toastStyles } from '../../utils/utils';
import {
  ACTIVITY_LIBRARY_ROUTE,
  APPOINTMENTS_ROUTE,
  CALENDAR_SETTINGS_ROUTE,
  CHAT_ROUTE,
  CLIENTS_ROUTE,
  LOGIN_ROUTE,
  MANAGE_TEAM_ROUTE,
  TEAM_CLIENTS_ROUTE,
  VIEW_BOOKINGS_ROUTE,
} from '../../utils/routes';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import LanguageSwitcher from '../LanguageSwitcher/LanguageSwitcher';
import UserSkelton from '../User/UserSkelton';
import {
  IFolderType,
  clearFolder,
  closeDeleteFolderModalOnSuccess,
  deleteFolder,
  setActivityLibraryBlock,
} from '../../redux/activityLibrary/activityLibrarySlice';
import CustomModal from '../Common/CustomModal';
import DeleteActivityModal from '../ActivityLibrary/DeleteActivity';
import { setChatNotificationStatus } from '../../redux/chat/chatSlice';
import createPermissionCheckers from '../../permissions-validation/createPermissionCheckers';
import { UserPermissionType } from '../../types/commonTypes';
import { brandTagKey } from '../../config/constants';
import { logout } from '../../redux/GlobalAction';
import { ClientType } from '../../types/client';

const TopBarContainer = styled(Box)((props) => ({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  justifyContent: 'space-between',
  [props.theme.direction === 'ltr' ? 'paddingRight' : 'paddingLeft']: '32px',
}));

const StyledDeleteButton = styled(Button)(() => ({
  padding: '8px 12px !important',
  gap: '10px',
  borderRadius: '10px',
  border: '2px solid var(--Alert-Sunset-Orange, #FF9A9A) !important',
  fontFamily: 'Satoshi',
  fontSize: '15px',
  fontWeight: 700,
  lineHeight: '20px ',
  '& span': {
    background:
      'var(--Alert-Sunset-Orange, linear-gradient(175deg, #FF9A9A -9.81%, #FF5858 96.32%))',
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
}));

const Divider = styled(Box)(() => ({
  margin: 'auto 24px',
  width: '1px',
  background: '#EEE',
  height: '36px',
  '@media (max-width: 920px)': {
    margin: 'auto 12px',
  },
}));

const StyledBackButton = styled(Button)(() => ({
  borderRadius: '15px',
  border: '2px solid var(--Brand-Cadet-Blue, #6599A5)',
  fontSize: '15px',
  padding: '0 6px 0 6px !important',
  height: '36px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& span': {
    background: 'linear-gradient(138deg, #6599A5 24.11%, #3B667E 83.2%)',
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    fontFamily: 'Satoshi',
    fontSize: '17px',
    fontWeight: 700,
    lineHeight: '22px',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    display: 'block',
    maxWidth: '150px',
    padding: '0 6px 0 6px !important',
    '@media (max-width: 920px)': {
      maxWidth: '75px',
    },
  },
  '&:hover': {
    border: '2px solid var(--Brand-Cadet-Blue, #6599A5)',
  },
}));

const EllipsisTypography = styled(Typography)`
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  display: block;
  padding-left: 8px;
  padding-right: 8px;
  color: ${({ theme }) => theme.palette.primary.main};
`;

const BodyRegularTypography = styled(EllipsisTypography)<{ isMediumScreen: boolean }>`
  font-size: 15px;
  max-width: ${({ isMediumScreen }) => (isMediumScreen ? '90px' : '200px')};
`;

const BodyBoldTypography = styled(EllipsisTypography)<{ isMediumScreen: boolean }>`
  font-family: 'Satoshi';
  font-size: 17px;
  font-weight: 700;
  line-height: 22px;
  max-width: ${({ isMediumScreen }) => (isMediumScreen ? '50px' : '300px')};
`;

const StyledTitle = styled(Typography)`
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  display: block;
  @media (max-width: 920px) {
    max-width: 90px;
  }
`;

export const StyledCard = styled(Card)`
  border-radius: 5px;
  padding: 3px 20px 3px 20px;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  margin: 0 10px 0 10px;
  text-align: center;
  box-shadow: none;
  max-width: 120px;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  color: #57b15a;
  background-color: rgba(87, 177, 90, 0.15);
`;

// this top includes the title of the page and the user icon
// ADD CLIENT button is only shown in the clients page -> derived from the route

interface ITopBarProps {
  hideBreadCrumb?: boolean;
}

const TopBar = ({ hideBreadCrumb }: ITopBarProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const coach = useAppSelector((state) => state.coach.coach);
  const coachLoading = useAppSelector((state) => state.coach.loading);
  const selectedClient = useAppSelector((state) => state.client.selectedClient);
  const ClientLoading = useAppSelector((state) => state.client.loading);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('lg'));
  const { t } = useTranslation();
  const { coachConfigurations, coachConfigurationsLoading } = useAppSelector(
    (state) => state.booking
  );

  const { state: locationState } = useLocation();

  const pathParts = location.pathname.split('/');
  const firstPathPart = `/${pathParts[1]}`;
  // regex for client path (e.g. /clients/1 {whatever}})
  const clientPathPattern = new RegExp(
    [
      '^\\/clients\\/',
      '([0-9a-fA-F]{8}',
      '-[0-9a-fA-F]{4}',
      '-[0-9a-fA-F]{4}',
      '-[0-9a-fA-F]{4}',
      '-[0-9a-fA-F]{12})',
      '(\\?.*)?',
    ].join('')
  );
  // client Avatar and Name is only shown in the client page
  const isClientPath = clientPathPattern.test(location.pathname);
  // Add client button is only shown in the clients page
  const isClientsPath = location.pathname === CLIENTS_ROUTE;
  const { id, groupid, groupname } = useParams();
  const isAppointmentsPath = location.pathname === APPOINTMENTS_ROUTE;
  const isAcitivtyLibraryPath = location.pathname === ACTIVITY_LIBRARY_ROUTE;
  const isViewBookingsPath = location.pathname === VIEW_BOOKINGS_ROUTE;
  const isBookingSettingsPath = location.pathname === CALENDAR_SETTINGS_ROUTE;
  const isTeamClientsPath = location.pathname.includes(`${MANAGE_TEAM_ROUTE}/`);
  const folderName = useAppSelector((state) => state.activityLibrary.folder.name);
  const folderType = useAppSelector((state) => state.activityLibrary.folder.folderType);
  const title = getTitle(firstPathPart);
  const userRole = useAppSelector((state) => state.coach.role);
  const admin = useAppSelector((state) => state.coach.admin);
  const coachId = useAppSelector((state) => state.coach.coach?.id) as string;
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const folderId = useAppSelector((state) => state.activityLibrary.folder.id);
  const emptyTrashLoading = useAppSelector((state) => state.activityLibrary.deleteFolderLoading);
  const emptyTrashSuccess = useAppSelector((state) => state.activityLibrary.isDeleteFolderSuccess);
  const { isMessageReceived, totalUnreadChatCount } = useAppSelector((state) => state.chat);
  const permissionCheckers = createPermissionCheckers();
  const { checkPermissions } = permissionCheckers;
  const linkToCopy = `${process.env.REACT_APP_AUTH_SERVICE}/u/${coachConfigurations.url}`;

  const logoutHandler = () => {
    // handle logout
    dispatch(logout());
    const brandTag = localStorage.getItem(brandTagKey);
    navigate(brandTag ? `${LOGIN_ROUTE}/${brandTag}` : LOGIN_ROUTE);
  };

  const breadCrumbClickHandler = () => {
    // can replace switch with future routes
    if (isClientPath) {
      navigate(CLIENTS_ROUTE);
    }
    if (folderName || folderType === IFolderType.TRASH) {
      dispatch(setActivityLibraryBlock());
      dispatch(clearFolder());
    }
    if (isTeamClientsPath) {
      navigate(MANAGE_TEAM_ROUTE);
    }
  };

  const handleCloseModal = () => {
    setIsDeleteModalOpen(false);
  };

  const handleDelete = () => {
    dispatch(deleteFolder({ folderId, coachId, isEmptyTrash: true }));
  };

  const navigateBookingSettings = () => {
    navigate(CALENDAR_SETTINGS_ROUTE);
  };

  useEffect(() => {
    if (emptyTrashSuccess) {
      handleCloseModal();
      dispatch(closeDeleteFolderModalOnSuccess());
    }
  }, [emptyTrashSuccess]);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(linkToCopy);
    toast.success(t('common.copied'), toastStyles.success);
  };

  return (
    <TopBarContainer>
      <Box display="flex" gap="12px" alignItems="center" px={isMediumScreen ? 4 : 2}>
        {!hideBreadCrumb && (
          <Box onClick={breadCrumbClickHandler} sx={{ cursor: 'pointer' }}>
            <StyledTitle variant="h6" color="primary">
              {title}
            </StyledTitle>
          </Box>
        )}
        <Box display="flex" gap="12px" alignItems="center">
          {(isClientPath || folderName || isTeamClientsPath) &&
            checkPermissions(
              [
                UserPermissionType.CORE_MODULE_VIEW_SINGLE_CLIENT,
                UserPermissionType.CORE_MODULE_VIEW_CLIENT_CONCENT,
                UserPermissionType.CLIENT_GROUP_MODULE_VIEW_CLIENT_GROUP,
              ],
              false
            ) && (
              <img
                src="/assets/icons/arrow-pointer.svg"
                alt="chevron-right"
                style={{
                  width: '24px',
                  height: '24px',
                }}
              />
            )}
          {folderName && isAcitivtyLibraryPath && (
            <Typography variant="h6" color="primary">
              {folderName}
            </Typography>
          )}

          {isClientPath &&
            ClientLoading &&
            checkPermissions([
              UserPermissionType.CORE_MODULE_VIEW_SINGLE_CLIENT,
              UserPermissionType.CORE_MODULE_VIEW_CLIENT_CONCENT,
            ]) && <UserSkelton />}

          {isTeamClientsPath &&
            checkPermissions([UserPermissionType.CORE_MODULE_GET_CLIENTS]) &&
            locationState && (
              <UserNameAvatar
                user={{
                  firstName: locationState.firstName,
                  lastName: locationState.lastName || '',
                  profilePic: locationState.profilePic,
                  email: locationState.email,
                }}
                type={UserType.CLIENT}
                logoutHandler={() => {}}
                isBreadcrumb
              />
            )}

          {selectedClient &&
            isClientPath &&
            !ClientLoading &&
            checkPermissions([
              UserPermissionType.CORE_MODULE_VIEW_SINGLE_CLIENT,
              UserPermissionType.CORE_MODULE_VIEW_CLIENT_CONCENT,
            ]) && (
              <Box display="flex" flexDirection="row" alignItems="center">
                <UserNameAvatar
                  user={{
                    firstName: groupid && groupname ? groupname : selectedClient.firstName,
                    lastName:
                      (groupid && groupname) || selectedClient.clientType === ClientType.GROUP
                        ? ''
                        : selectedClient.lastName || '',

                    profilePic: groupid && groupname ? '' : selectedClient.profilePic,
                    email: selectedClient.email,
                    surname: groupid && groupname ? groupname : selectedClient.surname,
                  }}
                  type={UserType.CLIENT}
                  logoutHandler={() => {}}
                  isBreadcrumb
                />
                {(selectedClient.clientType === ClientType.GROUP || groupid) && (
                  <StyledCard>{t('addNewClient.group')}</StyledCard>
                )}
                {id &&
                  groupid &&
                  groupname &&
                  selectedClient?.clientType === ClientType.INDIVIDUAL && (
                    <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
                      <img
                        src="/assets/icons/arrow-pointer.svg"
                        alt="chevron-right"
                        style={{
                          width: '24px',
                          height: '24px',
                        }}
                      />
                      <UserNameAvatar
                        user={{
                          firstName: selectedClient?.firstName,
                          lastName:
                            selectedClient?.lastName &&
                            selectedClient?.clientType === ClientType.INDIVIDUAL
                              ? selectedClient?.lastName
                              : '',
                          profilePic: selectedClient?.profilePic ? selectedClient?.profilePic : '',
                          email: selectedClient?.email,
                          surname: selectedClient?.surname,
                        }}
                        type={UserType.CLIENT}
                        logoutHandler={() => {}}
                        isBreadcrumb
                      />
                    </Box>
                  )}
              </Box>
            )}
        </Box>
      </Box>

      <Box display="flex">
        {isBookingSettingsPath && coachConfigurationsLoading && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              textAlign: 'center',
              minWidth: isMediumScreen ? '100px' : '250px',
              width: '100%',
            }}
          >
            <Skeleton variant="text" width="15%" height={25} />
            <Skeleton variant="text" width="75%" height={25} />
            <Skeleton variant="text" width="5%" height={25} />
          </Box>
        )}
        {isBookingSettingsPath && coachConfigurations.activeStep < 0 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              textAlign: 'center',
              flexDirection: 'row',
              height: '36px',
              margin: 'auto',
            }}
          >
            <BodyRegularTypography isMediumScreen={isMediumScreen}>
              {t('bookings.bookingSettings.YourBookingPageUrl')}
            </BodyRegularTypography>
            <BodyBoldTypography
              isMediumScreen={isMediumScreen}
              sx={{
                direction: 'ltr',
              }}
            >
              {linkToCopy}
            </BodyBoldTypography>
            <Tooltip title={`${t('bookings.bookingSettings.YourBookingPageUrl')} : ${linkToCopy}`}>
              <Box>
                <button
                  type="button"
                  style={{ background: 'transparent', border: 'none' }}
                  onClick={copyToClipboard}
                >
                  <img
                    src="/assets/icons/bookings-copy.svg"
                    alt="bookings"
                    style={{ cursor: 'pointer' }}
                  />
                </button>
              </Box>
            </Tooltip>
          </Box>
        )}
        {isViewBookingsPath &&
          coachConfigurations.activeStep < 0 &&
          checkPermissions(UserPermissionType.BOOKINGS_MODULE_VIEW_COACH_CONFIGURATIONS) && (
            <Box display="flex" alignItems="center">
              <StyledBackButton onClick={navigateBookingSettings}>
                <Box py={0} display="flex" justifyContent="center">
                  <img
                    src="/assets/icons/settings-bold.svg"
                    alt="booking-settings"
                    height="20px"
                    width="20px"
                  />
                </Box>
                <span>{t('common.modules.bookingSettings')}</span>
              </StyledBackButton>
            </Box>
          )}
        {folderType === IFolderType.TRASH &&
          userRole === UserType.COACH &&
          checkPermissions([
            UserPermissionType.ACTIVITY_LIBRARY_MODULE_DELETE_ACTIVITY_LIBRARY,
          ]) && (
            <Box margin="auto" display="flex" gap="12px" mr={3}>
              <StyledDeleteButton variant="outlined" onClick={() => setIsDeleteModalOpen(true)}>
                <Box
                  component="img"
                  src="/assets/icons/trash-bin-large.svg"
                  alt="plus-icon"
                  width={20}
                  height={20}
                />
                {isSmallScreen ? (
                  <Tooltip title="Clear Trash">
                    <span>{`${t('activityLibrary.trash.clearTrash').substring(0, 3)}...`}</span>
                  </Tooltip>
                ) : (
                  <span>{t('activityLibrary.trash.clearTrash')}</span>
                )}
              </StyledDeleteButton>
              {isDeleteModalOpen && (
                <CustomModal isOpen={isDeleteModalOpen} xs={3.3}>
                  <DeleteActivityModal
                    handleDelete={handleDelete}
                    handleDismiss={handleCloseModal}
                    isAcitivtyLibraryTrashPath={folderType === IFolderType.TRASH}
                    loading={emptyTrashLoading}
                    loadingText={t('activityLibrary.filled.emptyingTrash')}
                  />
                </CustomModal>
              )}
            </Box>
          )}
        {isClientsPath && userRole === UserType.COACH}
        {isAppointmentsPath && (
          <Box margin="auto">
            <Button
              variant="outlined"
              sx={{ px: '32px', textTransform: 'capitalize' }}
              onClick={() => navigate('/appointments/new')}
            >
              <img
                src="/assets/icons/plus.svg"
                alt="plus-icon"
                style={{ height: '13px', width: '13px', marginRight: '10px' }}
              />
              Create New Appointment
            </Button>
          </Box>
        )}
        {checkPermissions(UserPermissionType.CHAT_MODULE_VIEW_CHAT) ? (
          <>
            <Divider />
            <Box
              display="flex"
              justifyContent="flex-end"
              alignItems="center"
              data-testid="topbar-notification"
              onClick={() => {
                navigate(CHAT_ROUTE);
                dispatch(setChatNotificationStatus(false));
              }}
              sx={{ cursor: 'pointer' }}
              position="relative"
            >
              <Badge badgeContent={totalUnreadChatCount} color="error" overlap="circular">
                <img src="/assets/icons/chat-round.svg" alt="chat-round" />
              </Badge>
              {isMessageReceived && (
                <img
                  src="/assets/icons/unread-notification.svg"
                  alt="unread-notification"
                  style={{ position: 'absolute', top: '8px', right: '0' }}
                />
              )}
            </Box>
          </>
        ) : null}
        {checkPermissions(UserPermissionType.NOTIFICATION_MODULE_VIEW_NOTIFICATIONS) ? (
          <>
            <Divider />
            <Box
              display="flex"
              justifyContent="flex-end"
              alignItems="center"
              data-testid="topbar-notification"
            >
              <Notification
                onClick={() => {
                  // console.log('Notification Clicked');
                }}
              />
            </Box>
            <Divider />
          </>
        ) : (
          <Divider />
        )}
        {checkPermissions(UserPermissionType.CORE_MODULE_CHANGE_LANGUAGE) && (
          <>
            <Box
              display="flex"
              justifyContent="flex-end"
              alignItems="center"
              data-testid="topbar-notification"
            >
              <LanguageSwitcher />
            </Box>
            <Divider />
          </>
        )}
        <Box
          display="flex"
          justifyContent="flex-start"
          alignItems="center"
          data-testid="topbar-user-icon"
        >
          {coachLoading && <UserSkelton />}
          {coach && !coachLoading && userRole === UserType.COACH && (
            <UserNameAvatar user={coach} type={UserType.COACH} logoutHandler={logoutHandler} />
          )}
          {userRole === UserType.ADMIN && admin && (
            <UserNameAvatar
              user={admin as ILoggedUser}
              type={UserType.COACH}
              logoutHandler={logoutHandler}
            />
          )}
        </Box>
      </Box>
    </TopBarContainer>
  );
};

export default TopBar;
